/* eslint-disable max-len */
import type {RefObject} from 'react';

/**
 * The `BookProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface BookProps {
    /**
     * A string representing the CSS class to be applied to the BookIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#666'
     */
    color1?: string;
    /**
     * A number or string representing the height of the BookIcon element.
     *
     * @default 18
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Book'
     */
    testId?: string;
    /**
     * A number or string representing the height of the BookIcon element.
     *
     * @default 18
     */
    width?: number | string;
}

/**
 * The `Book` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `BookProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the BookIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the BookIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the BookIcon element.
 * @param props.ref       A ref to the SVG element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const BookComponent = <Book className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Book = ({
    className = '',
    color1 = '#666',
    height = 18,
    ref,
    testId = 'Book',
    width = 18
}: BookProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 18 18"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M3 14.63a1.87 1.87 0 0 1 1.88-1.88H15" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M4.88 1.5H15v15H4.88A1.87 1.87 0 0 1 3 14.63V3.38A1.9 1.9 0 0 1 4.88 1.5" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
);

Book.displayName = 'Book';